import React, { useCallback } from 'react';

import {
  FiltrosCompras,
  ItensDistintosDoComprador,
  fetchItensDistintosDoComprador,
} from '../../../api/comprasDashboardApi';
import ModalDeDetalhe, { useDetalhePaginado } from './ModalDeDetalhe';

/**
 * As peças que compõem o "Itens distintos" do card de um comprador — at-115.
 *
 * 🔴 A lista agrupa por **código**, igual ao número do card (`distinct: ['codigo']`). O card
 * vizinho "Itens mais requisitados" agrupa por **descrição normalizada**, e os dois números são
 * diferentes: dois códigos com a mesma descrição contam 2 aqui e 1 lá. Reaproveitar aquela rota
 * faria a lista abrir com menos linhas do que o número clicado, que é a classe de divergência que
 * a at-110 existiu para fechar.
 *
 * ⚠️ Leva o filtro do painel **e** o status escolhido no próprio card: foi com os dois que a
 * contagem foi feita.
 */

const POR_PAGINA = 25;

export interface ItensAbertos {
  id: string;
  nome: string;
}

interface Props {
  aberto: ItensAbertos;
  filtros: FiltrosCompras;
  /** Os status escolhidos no card. Vazio = todos. */
  situacoes?: string[];
  onFechar: () => void;
}

/** Três colunas: o que identifica a peça e quanto dela foi pedido. */
const COLUNAS = ['Código', 'Produto', 'Requisições'];

export default function ItensDoCompradorModal({ aberto, filtros, situacoes, onFechar }: Props) {
  const carregar = useCallback(
    (pagina: number) =>
      fetchItensDistintosDoComprador(
        { ...filtros, comprador: aberto.id, situacoes },
        pagina,
        POR_PAGINA,
      ) as Promise<ItensDistintosDoComprador>,
    // ⚠️ A lista de status entra como TEXTO nas dependências: array novo a cada render refaria a
    // carga sempre — a mesma armadilha que o hook do painel já registra.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [aberto.id, filtros, (situacoes ?? []).join(',')],
  );

  const { dados, carregando, erro, pagina, setPagina } = useDetalhePaginado(carregar);

  const total = dados?.total ?? 0;
  const itens = dados?.itens ?? [];

  return (
    <ModalDeDetalhe
      titulo={`Peças pedidas por ${aberto.nome}`}
      legenda={
        carregando
          ? 'Carregando...'
          : erro
            ? 'Não foi possível carregar'
            : `${total.toLocaleString('pt-BR')} ${total === 1 ? 'código de peça' : 'códigos de peça'} diferentes no período`
      }
      carregando={carregando}
      erro={erro}
      erroTexto={
        'Não foi possível carregar as peças deste comprador. Isto não significa que ele não tenha ' +
        'pedido nenhuma — tente de novo e, se persistir, acione a TI.'
      }
      vazio={itens.length === 0}
      vazioTexto="Nenhuma peça pedida por este comprador no período e no recorte escolhidos."
      colunas={COLUNAS}
      classeTabela="dcr-tabela-itens-comprador"
      paginacao={{
        pagina,
        ultimaPagina: Math.max(1, Math.ceil(total / POR_PAGINA)),
        onMudar: setPagina,
      }}
      onFechar={onFechar}
    >
      {itens.map((i) => (
        <div key={i.codigo ?? '(sem código)'} className="dcr-linha">
          {/*
            ⚠️ Item sem código é CONTADO pelo card (o `distinct` trata nulo como um valor), então
            aparece aqui dizendo o que é — escondê-lo faria a lista ter uma linha a menos que o
            número, e ninguém saberia qual das duas está certa.
          */}
          <div className="dcr-celula" title={i.codigo ?? ''}>
            {i.codigo ?? <span className="dcr-ausente">sem código</span>}
          </div>
          <div className="dcr-celula" title={i.descricao ?? ''}>
            {i.descricao ?? <span className="dcr-ausente">sem descrição</span>}
          </div>
          <div className="dcr-celula">{i.requisicoes.toLocaleString('pt-BR')}</div>
        </div>
      ))}
    </ModalDeDetalhe>
  );
}
